import React from "react";
import CustomModal from "./CustomModal";
import LoadingButton from "./LoadingButton";

/**
 * The one confirm dialog: a question, a sentence explaining what happens, and
 * two buttons flush right with Cancel first.
 *
 * It replaces the nine hand-rolled delete popups, each of which was a
 * react-modal with its own `customStyles` object, its own ✕ glyph in a
 * col-md-6, its own centred <p>, and its own idea of which button was the
 * destructive one. The question now lives in the title (a heading, not body
 * copy) so a screen reader announces what is being confirmed, and nothing but
 * `onConfirm` can close the box through the primary action.
 *
 * `onConfirm` is a LoadingButton action, so the contract is that button's:
 * return the promise (resolve → success face, reject → error face), or return
 * `false` to abort straight back to idle. Closing is the CALLER's business —
 * every existing handler already calls its own `closeModal()` in `.then`, and
 * a component that closed itself would cut the success face short.
 *
 * @param tone  "danger" for a delete/cancel, "primary" for everything else.
 *              Both are red in this theme; the distinction is semantic and
 *              keeps a destructive fill re-huable on its own token.
 */
const ConfirmDialog = ({
  isShow,
  onHide = () => { },
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  successLabel = "Done",
  errorLabel = "Try again",
  tone = "primary",
  onConfirm,
  children,
}) => (
  <CustomModal
    isShow={isShow}
    onHide={onHide}
    heading={title}
    description={description}
    size="sm"
    showClose={false}
    footer={
      <>
        <button
          type="button"
          className="app-dialog-btn app-dialog-btn-ghost"
          onClick={onHide}
        >
          {cancelLabel}
        </button>
        <LoadingButton
          className={`app-dialog-btn app-dialog-btn-${tone === "danger" ? "danger" : "primary"}`}
          onAction={onConfirm}
          successLabel={successLabel}
          errorLabel={errorLabel}
        >
          {confirmLabel}
        </LoadingButton>
      </>
    }
  >
    {children}
  </CustomModal>
);

export default ConfirmDialog;
